/**
 * Markup templating.
 *
 * `html` is a tagged template that escapes every interpolated value, unless
 * it is already markup (the result of `html` or `raw`). Arrays are joined,
 * `null`, `undefined` and `false` render nothing, so conditions and lists
 * read naturally:
 *
 *     html`<ul>${items.map((item) => html`<li>${item.label}</li>`)}</ul>`
 *     html`${project.award && html`<span>${project.award.label}</span>`}`
 *
 * Because `false` renders nothing, a boolean meant as an attribute value is
 * passed as a string: aria-pressed="${String(selected)}".
 *
 * @module lib/html
 */

const MARKUP = Symbol('markup');

/**
 * @typedef {object} Markup
 * @property {string} value - Trusted HTML.
 */

/**
 * Marks a string as trusted HTML: it will not be escaped again.
 * Only for markup built by this code base, never for user content.
 *
 * @param {string} value
 * @returns {Markup}
 */
export function raw(value) {
    return { [MARKUP]: true, value: String(value) };
}

/**
 * Escapes the five characters that matter in text and attribute values.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function escapeHtml(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function stringify(value) {
    if (value === null || value === undefined || value === false) {
        return '';
    }

    if (Array.isArray(value)) {
        return value.map(stringify).join('');
    }

    if (typeof value === 'object' && value[MARKUP]) {
        return value.value;
    }

    return escapeHtml(value);
}

/** @type {WeakMap<TemplateStringsArray, string[]>} */
const compacted = new WeakMap();

/**
 * Templates are indented for reading; the markup is not. Whitespace that
 * holds a line break is dropped where it touches a tag or a value, so a line
 * break between two tags means "nothing in between". Spaces on a single
 * line are kept, and interpolated values are never touched (a <pre> keeps
 * its newlines when they are passed as values).
 *
 * @param {TemplateStringsArray} strings
 * @returns {string[]}
 */
function compact(strings) {
    if (!compacted.has(strings)) {
        compacted.set(
            strings,
            strings.map((chunk) =>
                /^\s*$/.test(chunk) && chunk.includes('\n')
                    ? ''
                    : chunk
                          .replace(/>\s*\n\s*/g, '>')
                          .replace(/\s*\n\s*</g, '<')
                          .replace(/^\s*\n\s*/, '')
                          .replace(/\s*\n\s*$/, ''),
            ),
        );
    }

    return compacted.get(strings);
}

/**
 * @param {TemplateStringsArray} strings
 * @param {...unknown} values
 * @returns {Markup}
 */
export function html(strings, ...values) {
    const chunks = compact(strings);
    let out = chunks[0];

    values.forEach((value, index) => {
        out += stringify(value) + chunks[index + 1];
    });

    return raw(out);
}

/**
 * Turns markup back into a string (to hand it to innerHTML).
 *
 * @param {Markup | Markup[] | string} markup
 * @returns {string}
 */
export function renderToString(markup) {
    return stringify(markup);
}
